import { useNavigate, Routes, Route, Navigate } from "react-router-dom";
import { LoginCallback, Security } from "@okta/okta-react";
import OktaAuth, { toRelativeUrl } from "@okta/okta-auth-js";
import { authClient } from "@lib/okta";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SecureRoute } from "@components/SecureRoute";
import { Loader } from "@components/Loader";
import { StartPage } from "@pages/StartPage";
import { DetailPage } from "@pages/DetailPage";
import { GeneralPage } from "@pages/GeneralPage";
import { ErrorPage } from "@pages/ErrorPage";
import { UserProvider } from "@providers/UserProvider";
import { ConfigProvider } from "@providers/ConfigProvider";
import { NotificationsProvider } from "@providers/NotificationsProvider";
import { routePrefixLanguages } from "./i18n";
import { SearchResultsPage } from "@pages/SearchResultsPage";
import { PeopleFinderPage } from "@pages/PeopleFinderPage";
import { VacanciesPage } from "@pages/VacanciesPage";
import { PeopleFinder } from "./constants";
import { useUsersnap } from "@hooks/useUsersnap";
import { SettingsPage } from "@pages/SettingsPage";
import { PreviewLogin } from "@pages/PreviewLogin";
import { PreviewLoginRelay } from "@components/PreviewLoginRelay";
import { isPreviewLoginHandoff } from "@utils/previewLoginHandoff";
import { AppErrorBoundary } from "@components/AppErrorBoundary";
import { VacancyDetailPage } from "@pages/VacancyDetailPage";

function App() {
  useUsersnap(
    import.meta.env.VITE_USERSNAP_API_KEY as string,
    import.meta.env.VITE_USERSNAP_API_KEY !== "production"
  );
  const navigate = useNavigate();

  const restoreOriginalUri = async (
    _oktaAuth: OktaAuth,
    originalUri: string
  ) => {
    await navigate(toRelativeUrl(originalUri ?? "/", window.location.origin));
  };

  // Generate specific routes for language start pages
  const languageSpecificStartRoutes = routePrefixLanguages.map((lang) => ({
    path: `/${lang}`,
    element: <StartPage />,
    isPrivate: true,
  }));

  // Generate specific routes for language-prefixed general pages
  const languageSpecificGeneralRoutes = routePrefixLanguages.map((lang) => ({
    path: `/${lang}/:storyblokSlug/*`,
    element: <GeneralPage />,
    isPrivate: true,
  }));

  // Route definitions
  const APP_ROUTES = [
    { path: "/", element: <StartPage />, isPrivate: true },
    ...languageSpecificStartRoutes,
    {
      path: "/vacancies",
      element: <VacanciesPage />,
      isPrivate: true,
    },
    {
      path: "/:lang/vacancies",
      element: <VacanciesPage />,
      isPrivate: true,
    },
    {
      path: "/vacancies/:vacancySlug",
      element: <VacancyDetailPage />,
      isPrivate: true,
    },
    {
      path: "/:lang/vacancies/:vacancySlug",
      element: <VacancyDetailPage />,
      isPrivate: true,
    },
    {
      path: "/settings",
      element: <SettingsPage />,
      isPrivate: true,
    },
    {
      path: "/:lang/settings",
      element: <SettingsPage />,
      isPrivate: true,
    },
    {
      path: `/${PeopleFinder}`,
      element: <PeopleFinderPage />,
      isPrivate: true,
    },
    {
      path: `/:lang/${PeopleFinder}`,
      element: <PeopleFinderPage />,
      isPrivate: true,
    },
    { path: "/news/:storyblokSlug", element: <DetailPage />, isPrivate: true },
    {
      path: "/news/leadership/:storyblokSlug",
      element: <DetailPage />,
      isPrivate: true,
    },
    {
      path: "/search/:chatId",
      element: <SearchResultsPage />,
      isPrivate: true,
    },
    {
      path: "/:lang/search/:chatId",
      element: <SearchResultsPage />,
      isPrivate: true,
    },
    {
      path: "/:lang/news/:storyblokSlug",
      element: <DetailPage />,
      isPrivate: true,
    },
    {
      path: "/:lang/news/leadership/:storyblokSlug",
      element: <DetailPage />,
      isPrivate: true,
    },
    // Language-prefixed General pages (explicitly enumerated to avoid
    // treating any first segment as a language on subpages)
    ...languageSpecificGeneralRoutes,
    { path: "/:storyblokSlug/*", element: <GeneralPage />, isPrivate: true },
  ];

  return (
    <AppErrorBoundary>
      <Security oktaAuth={authClient} restoreOriginalUri={restoreOriginalUri}>
        <ConfigProvider>
          <main>
            <Routes>
              <Route
                path="login/callback"
                element={
                  // A preview popup handoff relays its tokens to the editor iframe and closes;
                  // normal logins use the standard Okta callback. The flag is set by the popup
                  // (PreviewLogin) before the Okta redirect, so it is present on this load.
                  isPreviewLoginHandoff() ? (
                    <PreviewLoginRelay />
                  ) : (
                    <LoginCallback
                      loadingElement={
                        <div className="flex h-screen items-center justify-center">
                          <Loader />
                        </div>
                      }
                    />
                  )
                }
              />
              <Route path="preview-login" element={<PreviewLogin />} />
              {APP_ROUTES.map(({ path, element, isPrivate }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    isPrivate ? (
                      <SecureRoute>
                        <UserProvider>
                          <NotificationsProvider>
                            {element}
                          </NotificationsProvider>
                        </UserProvider>
                      </SecureRoute>
                    ) : (
                      element
                    )
                  }
                />
              ))}
              <Route
                path="/:lang/forbidden"
                element={<ErrorPage errorCode="403" />}
              />
              <Route
                path="/:lang/404"
                element={
                  <SecureRoute>
                    <UserProvider>
                      <NotificationsProvider>
                        <ErrorPage errorCode="404" />
                      </NotificationsProvider>
                    </UserProvider>
                  </SecureRoute>
                }
              />
              <Route
                path="/forbidden"
                element={<ErrorPage errorCode="403" />}
              />
              <Route
                path="/404"
                element={
                  <SecureRoute>
                    <UserProvider>
                      <NotificationsProvider>
                        <ErrorPage errorCode="404" />
                      </NotificationsProvider>
                    </UserProvider>
                  </SecureRoute>
                }
              />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
            <ReactQueryDevtools initialIsOpen={false} />
          </main>
        </ConfigProvider>
      </Security>
    </AppErrorBoundary>
  );
}

export default App;
